// src/hooks/useLlmRecomendations.ts
//
// The three consult-record drafts, generated from a live consultation.
//
// ⚠️ THESE NO LONGER TOUCH `talk`. `POST {talk}/api/generate-{medical-history,medication,
// test-order}` DO NOT EXIST — the prompts moved into `xoe-agent` (ClickUp 86d3r82eh, M20/M21/M22)
// and `talk` holds no AI at all. Each hook now relays one generation through healthcare's
// consult-generation route; see `api/healthcare/ConsultGenerationApi.ts`.
//
// ⚠️ **THE `inputs` KEY NAMES ARE EACH PIPELINE'S CONTRACT AND THEY ARE NOT UNIFORM.** They were
// read off the three `build_user_message()` builders in
// `xoe-agent/app/generation/pipelines/consult_record_*.py`, not guessed from each other:
//
//     medical_history  patient, suggestion, transcripts, history_type,
//                      user_id, user_first_name, user_last_name              (7)
//     medication       patient, suggestion, transcripts, user_id             (4 — this prompt
//                      never took the practitioner's name, and adding it changes nothing)
//     test_order       patient, suggestion, test_type, transcripts,
//                      user_id, user_first_name, user_last_name              (7)
//
// A wrong or missing key does NOT error. Every builder renders an absent input as the literal
// six characters `undefined` into the prompt (JS `${undefined}` semantics, ported verbatim), so
// the model is asked to draft a record about a patient called `undefined` and answers plausibly.
// The section simply comes back empty or wrong, with a green log line at every hop.

import { useState, useRef } from "react";

// API
import { generate, runKey } from "../../api/healthcare/Consultation/ConsultGenerationApi";

// Types
import { Patient } from "../../types/healthcare/PatientTypes";
import { Transcript } from "../../types/appointments/ConsultTypes";
import { defaultMedication, Medication } from "../../types/healthcare/MedicationTypes";
import { Suggestion, MedicalHistoryType, TestType } from "../../utils/healthcare/SugestionUtils";
import type { UserUuid } from "../../types/identity";

/**
 * 120s, and it is the outermost number in a chain sized end to end.
 *
 *      90   the relay's own budget for all three keys (`ConsultGenerationController::timeouts()`)
 *   +  10   `services.xoe_agent.connect_timeout`
 *   = 100   healthcare's worst case
 *     105   what the relay is allowed
 *     120   this
 *
 * The worst case FELL on this path: `recordGenerator.js`'s `callAnthropic(payload, retries = 2)`
 * was up to three attempts at a 60s axios timeout — 180s plus backoff — for a call the browser
 * was already waiting on. One attempt at 90s is a tighter ceiling, and there is no retry at any
 * hop by design.
 */
const RECORD_TIMEOUT_MS = 120000;

/**
 * Hook for generating a medical history record from a suggestion.
 */
export function useMedicalHistoryGenerator(
	patient: Patient,
	suggestion: Suggestion,
	historyType: MedicalHistoryType,
	transcripts: Transcript[],
	userId: UserUuid,
	userFirstName: string,
	userLastName: string
) {
	const [isGenerating, setIsGenerating] = useState(false);
	const [generatedRecord, setGeneratedRecord] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const generationInProgressRef = useRef(false);

	const generateRecord = async () => {
		// Guard against multiple simultaneous calls
		if (isGenerating || generationInProgressRef.current) return;

		generationInProgressRef.current = true;
		setIsGenerating(true);
		setError(null);

		try {
			console.log('Calling server to generate medical history record');

			// ⚠️ THE DEFAULT MOVED HERE, AND IT USED TO LIVE IN `talk`.
			// `generateMedicalHistoryRecord()` resolved `suggestion.medical_history_record_type ||
			// 'diagnosis'` before building the prompt; nothing does that server-side any more.
			// The `historyType` prop has been accepted and NEVER SENT for the life of this hook —
			// a latent bug that only ever looked fine because talk re-derived the value from the
			// suggestion it was also given.
			//
			// It is BOTH an input and the pipeline key's suffix, and the KEY is the authority
			// agent-side: an unregistered type is a 422 from the relay's allow-list — loud and
			// immediate, on the first record of that type — rather than a record silently
			// missing that type's fields.
			const resolvedHistoryType = historyType || suggestion.medical_history_record_type || "diagnosis";

			const envelope = await generate<any>({
				pipeline: `consult_record.medical_history.${resolvedHistoryType}`,
				runKey: runKey(),
				patientId: patient.id,
				inputs: {
					patient,
					suggestion,
					transcripts,
					history_type: resolvedHistoryType,
					user_id: userId,
					user_first_name: userFirstName,
					user_last_name: userLastName,
				},
				timeoutMs: RECORD_TIMEOUT_MS,
			});

			// A 200 is not success — see `ConsultGenerationApi.generate()`. `data` is null on
			// every failure, so both halves of this are real.
			if (envelope.metadata?.status === "complete" && envelope.data) {
				setGeneratedRecord(envelope.data);
			} else {
				setError("Invalid response from server");
			}
		} catch (error: any) {
			console.error("Error generating medical history record:", error);
			setError(error.message || "Error generating record");
		} finally {
			setIsGenerating(false);
			generationInProgressRef.current = false;
		}
	};

	return { generateRecord, isGenerating, generatedRecord, error };
}

/**
 * Hook for generating a medication record from a suggestion.
 */
export function useMedicationGenerator(
	patient: Patient,
	suggestion: Suggestion,
	transcripts: Transcript[],
	userId: UserUuid
) {
	const [isGenerating, setIsGenerating] = useState(false);
	const [generatedRecord, setGeneratedRecord] = useState<Medication>(defaultMedication);
	const [error, setError] = useState<string | null>(null);
	const generationInProgressRef = useRef(false);

	const generateRecord = async () => {
		// Guard against multiple simultaneous calls
		if (isGenerating || generationInProgressRef.current) return;

		generationInProgressRef.current = true;
		setIsGenerating(true);
		setError(null);

		try {
			console.log('Calling server to generate medication record');

			const envelope = await generate<Medication>({
				// ONE key, not a family. `generateMedicationRecordPrompt()` took no type
				// argument and rendered one structure always, so the agent freezes one
				// `input_schema` — unlike medical history, which is keyed per type.
				pipeline: "consult_record.medication",
				runKey: runKey(),
				patientId: patient.id,
				// ⚠️ FOUR KEYS. No `user_first_name` / `user_last_name`: this prompt interpolates
				// `userId` into `prescribed_by` and never names the practitioner.
				inputs: {
					patient,
					suggestion,
					transcripts,
					user_id: userId,
				},
				timeoutMs: RECORD_TIMEOUT_MS,
			});

			if (envelope.metadata?.status === "complete" && envelope.data) {
				setGeneratedRecord(envelope.data);
			} else {
				setError("Invalid response from server");
			}
		} catch (error: any) {
			console.error("Error generating medication record:", error);
			setError(error.message || "Error generating record");
		} finally {
			setIsGenerating(false);
			generationInProgressRef.current = false;
		}
	};

	return { generateRecord, isGenerating, generatedRecord, error };
}

/**
 * Hook for generating a test order from a suggestion.
 */
export function useTestOrderGenerator(
	patient: Patient,
	suggestion: Suggestion,
	testType: TestType,
	transcripts: Transcript[],
	userId: UserUuid,
	userFirstName: string,
	userLastName: string
) {
	const [isGenerating, setIsGenerating] = useState(false);
	const [generatedRecord, setGeneratedRecord] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const generationInProgressRef = useRef(false);

	const generateRecord = async () => {
		// Guard against multiple simultaneous calls
		if (isGenerating || generationInProgressRef.current) return;

		generationInProgressRef.current = true;
		setIsGenerating(true);
		setError(null);

		try {
			console.log('Calling server to generate test order');

			// ⚠️ THE OTHER DEFAULT THAT USED TO LIVE IN `talk` — `generateTestOrder():547` resolved
			// `suggestion.test_type || 'laboratory'` and the agent receives only the resolved
			// value. Unlike `history_type` this is NOT part of the pipeline key: it is rendered
			// into the prompt twice (as `test_category` and as the `[Test Category]` section) and
			// the agent renders whatever it receives, so sending nothing puts the literal word
			// `undefined` into a clinical prompt with no error anywhere.
			const resolvedTestType = testType || suggestion.test_type || "laboratory";

			const envelope = await generate<any>({
				pipeline: "consult_record.test_order",
				runKey: runKey(),
				patientId: patient.id,
				inputs: {
					patient,
					suggestion,
					test_type: resolvedTestType,
					transcripts,
					user_id: userId,
					user_first_name: userFirstName,
					user_last_name: userLastName,
				},
				timeoutMs: RECORD_TIMEOUT_MS,
			});

			if (envelope.metadata?.status === "complete" && envelope.data) {
				setGeneratedRecord(envelope.data);
			} else {
				setError("Invalid response from server");
			}
		} catch (error: any) {
			console.error("Error generating test order:", error);
			setError(error.message || "Error generating record");
		} finally {
			setIsGenerating(false);
			generationInProgressRef.current = false;
		}
	};

	return { generateRecord, isGenerating, generatedRecord, error };
}
